import React, { Fragment, useState } from "react";
import { Drawer } from "../../../components/Drawer";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import { useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Button } from "../../../components/Button";
import { Tooltip } from "../../../utils/local-antd";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { AtiraToast } from "../../../utils/AtiraToast";
import { moduleActions } from "../../../redux/module/module.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { WarningModal } from "../../../components/WarningModal";
import { Rounded } from "../../../theme/Rounded";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons/faPaperclip";
import { ModuleInstanceAttachment } from "../../../model/module/types/module-instance-attachment/ModuleInstanceAttachment";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { ModuleInstanceAttachmentUpdateModal } from "./ModuleInstanceAttachmentUpdateModal";
import { isDateModuleInput } from "../module-definition/utils/moduleUtils";
import { standardDate } from "../../../utils/Date";
import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import isFunction from "lodash/isFunction";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { Direction } from "../../../model/shared/Direction.enum";

const EntryCard = styled(Flex)`
  flex-direction: column;
  border-radius: ${Rounded.md};
  background: ${({ theme }) => theme.lightSub};
  border: 1px solid ${({ theme }) => theme.sub};
  overflow: hidden;
`;

const RowHeader = styled(Text)`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  word-break: break-all;
`;

const RowActions = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.main};
  justify-content: flex-end;
`;

const StyledButton = styled(Button)`
  background-color: ${({ theme }) => theme.darkMain};
  border-radius: 0;
  color: ${({ theme }) => theme.white};
  margin: 0;
  padding: ${Spacing.s};
  &:first-of-type {
    border-inline-end: ${({ theme }) => `1px solid ${theme.sub}50`};
  }
`;

const StyledSpan = styled.span<{ direction?: Direction }>`
  direction: ${({ direction }) => direction || "auto"};
`;

type ModuleInstanceAttachmentsReadDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
  moduleInstance?: BaseModuleInstance | null;
  setSelectedInstance: ATVoidFunction<BaseModuleInstance>;
  onOpenCreateInstanceAttachmentModal: VoidFunction;
};

export const ModuleInstanceAttachmentsReadDrawer: React.FC<
  ModuleInstanceAttachmentsReadDrawerProps
> = ({
  open = false,
  onClose,
  moduleInstance,
  setSelectedInstance,
  onOpenCreateInstanceAttachmentModal,
}) => {
  const [selectedAttachment, setSelectedAttachment] =
    useState<ModuleInstanceAttachment | null>(null);
  const [detachLoading, setDetachLoading] = useState(false);
  const [detachWarningModalVisible, setDetachWarningModalVisible] =
    useState(false);
  const [updateAttachmentDrawerVisible, setUpdateAttachmentDrawerVisible] =
    useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const moduleInstancesPage = useAppSelector(
    moduleSliceSelectors.selectInstancesPage,
  );
  const moduleInstancesPageSize = useAppSelector(
    moduleSliceSelectors.selectInstancesPageSize,
  );
  const currentModuleDefinition = useAppSelector((s) =>
    moduleSliceSelectors.selectModuleDefinitionById(
      s,
      moduleInstance?.parentId!,
    ),
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const userModulePermissions = useAppSelector((s) =>
    userSliceSelectors.selectModulePermissionsById(
      s,
      currentModuleDefinition?._id || "",
    ),
  );

  const onDetach = (attachment: ModuleInstanceAttachment) => {
    setSelectedAttachment(attachment);
    setDetachWarningModalVisible(true);
  };

  const onUpdate = (attachment: ModuleInstanceAttachment) => {
    setSelectedAttachment(attachment);
    setUpdateAttachmentDrawerVisible(true);
  };

  const onCreateAttchment = () => {
    if (!moduleInstance) {
      return;
    }
    setSelectedInstance(moduleInstance);
    onOpenCreateInstanceAttachmentModal();
  };

  const detachInstance = async () => {
    try {
      if (!selectedAttachment) {
        return;
      }

      setDetachLoading(true);

      await dispatch(
        moduleActions.detachModuleInstanceFromEntry({
          userId,
          moduleDefinitionId: moduleInstance?.parentId!,
          moduleInstanceId: moduleInstance?._id!,
          moduleInstanceAttachementId: selectedAttachment._id!,
        }),
      ).unwrap();

      const { data: newInstances } = await dispatch(
        moduleActions.getModuleInstances({
          keyword: "",
          moduleDefinitionId: moduleInstance?.parentId!,
          meta: PageMeta.create({
            page: moduleInstancesPage - 1,
            count: moduleInstancesPageSize,
          }),
          userId,
        }),
      ).unwrap();

      const updatedModuleInstance = newInstances?.find(
        (mi) => mi._id === moduleInstance?._id,
      );

      if (isFunction(setSelectedInstance) && updatedModuleInstance) {
        setSelectedInstance(updatedModuleInstance);
      }

      setDetachWarningModalVisible(false);
      setSelectedAttachment(null);

      AtiraToast.success(t("modules.instances.delete.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDetachLoading(false);
    }
  };

  if (!moduleInstance) {
    return null;
  }

  return (
    <Drawer
      title={t("modules.instances.entries.read.drawer.title")}
      open={open}
      onClose={onClose}
      width={"35rem"}
    >
      <Flex flex={1} flexDirection="column" gap="m">
        {moduleInstance?.instanceAttachments?.length ? (
          <Fragment>
            {moduleInstance.instanceAttachments.map((attachment) => {
              const entryName =
                attachment.entry?.fields?.email ||
                attachment.entry?.fields?.phone ||
                attachment.entry?.fields?.name;

              return (
                <EntryCard key={attachment._id}>
                  <RowHeader>
                    <AtiraIcon icon={faPaperclip} color="main" />
                    <strong>{attachment?.name}</strong>
                  </RowHeader>

                  <Flex paddingInline="m" paddingBottom="s">
                    <Text>
                      <strong>{t("common.entry")}: </strong>
                      <span>{entryName}</span>
                    </Text>
                  </Flex>

                  <Flex flexDirection="column" gap="s" paddingInline="m">
                    {Object.entries(attachment?.fields || {}).map(
                      ([key, value]) => {
                        const currentInput =
                          currentModuleDefinition?.inputs?.find(
                            (inp) => inp.name === key,
                          );

                        const direction = [
                          InputTypes.EMAIL,
                          InputTypes.PHONE,
                          InputTypes.NUMBER,
                        ].includes(currentInput?.type!)
                          ? Direction.LTR
                          : undefined;

                        return (
                          <Text key={key}>
                            <strong>{currentInput?.label}: </strong>
                            <StyledSpan direction={direction}>
                              {isDateModuleInput(currentModuleDefinition!, key)
                                ? (standardDate(
                                    value as string,
                                    user,
                                    true,
                                  ) as string)
                                : (value?.toString() as string)}
                            </StyledSpan>
                          </Text>
                        );
                      },
                    )}
                  </Flex>

                  <Flex
                    alignItems="center"
                    gap="s"
                    justifyContent="space-between"
                    padding="m"
                  >
                    <Text fontWeight={"bold"}>{t("common.assignees")}</Text>

                    <AssigneeBanner
                      assignees={members?.filter((m) =>
                        attachment.entry?.assigneeIds?.includes(m._id),
                      )}
                    />
                  </Flex>

                  <RowActions>
                    <Tooltip title={t("common.edit")}>
                      <StyledButton
                        onClick={() => onUpdate(attachment)}
                        icon={faEdit}
                      />
                    </Tooltip>

                    <Tooltip title={t("common.delete")}>
                      <StyledButton
                        onClick={() => onDetach(attachment)}
                        icon={faTrash}
                      />
                    </Tooltip>
                  </RowActions>
                </EntryCard>
              );
            })}

            <WarningModal
              title={t("modules.instances.detach.modal.title", {
                name: selectedAttachment?.name,
              })}
              description={t("modules.instances.delete.modal.description")}
              onConfirm={detachInstance}
              isOpen={detachWarningModalVisible}
              onClose={() => setDetachWarningModalVisible(false)}
              loading={detachLoading}
            />

            <ModuleInstanceAttachmentUpdateModal
              attachment={selectedAttachment}
              open={updateAttachmentDrawerVisible}
              onClose={() => setUpdateAttachmentDrawerVisible(false)}
              setSelectedInstance={setSelectedInstance}
            />
          </Fragment>
        ) : (
          <AtiraEmpty
            description={t("modules.instances.entries.read.drawer.empty")}
            action={{
              title: t("modules.instances.entries.read.drawer.empty.action"),
              onClick: onCreateAttchment,
              hide: userModulePermissions?.CREATE !== true,
            }}
            iconHeight={"5rem"}
          />
        )}
      </Flex>
    </Drawer>
  );
};
